import { useState } from 'react';
import { Grid, Typography, Box, Button, IconButton } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import { DeleteOutlineOutlined } from '@mui/icons-material';
import { colors, MessageGroup, PageLoader, CustomPagination } from 'remote/global';
import { useTranslation } from 'react-i18next';
import { DataGridOverlay } from 'utils/component-utils';
import {
  useDeleteOperatorsUserMessagingGroupsMembersMutation,
  useOperatorsUserMessagingGroupsMembersQuery,
  useOperatorsUserMessagingGroupsQuery,
} from 'api/queries/operators';
import GroupEditForm from 'components/forms/MessagingFactory/GroupEditForm';
import AddMembersToGroupModal from 'components/modals/AddMembersToGroupModal';

function MessagesGroupEditView() {
  const [isAddMembersToGroupModalOpen, setIsAddMembersToGroupModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  const { data: messageGroups, isSuccess: isSuccessMessageGroups } =
    useOperatorsUserMessagingGroupsQuery();
  const {
    data: messageGroupMembers,
    isSuccess: isSuccessMessageGroupMembers,
    isLoading: isLoadingMessageGroupMembers,
  } = useOperatorsUserMessagingGroupsMembersQuery(id!);
  const deleteOperatorsUserMessagingGroupsMembersMutation =
    useDeleteOperatorsUserMessagingGroupsMembersMutation(id!);

  if (!isSuccessMessageGroups || !isSuccessMessageGroupMembers) return null;

  const activeGroup = messageGroups.find((group: MessageGroup) => group.id === Number(id));

  const handleRemoveGroupMember = async (userId: number) => {
    await deleteOperatorsUserMessagingGroupsMembersMutation.mutateAsync(String(userId));
  };

  const handlePageChange = (pageNo: number) => {
    setPage(pageNo);
  };

  const getTableRows = () => {
    return messageGroupMembers?.elements ?? [];
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('MessagesView.group.current.table.header.name') as string,
      flex: 2,
      renderCell: ({ row }) => {
        return (
          <Box>
            <Typography
              sx={{
                color: colors.gray2,
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              {row.contactInfo.firstName} {row.contactInfo.lastName}
            </Typography>
            <Typography
              sx={{
                color: colors.gray1,
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              {row.contactInfo.email}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'delete',
      align: 'right',
      headerName: ' ',
      flex: 2,
      renderCell: ({ row }) => {
        return (
          <Box>
            <IconButton
              size='large'
              aria-label='account of current user'
              aria-controls='menu-appbar'
              aria-haspopup='true'
              disabled={isLoadingMessageGroupMembers}
              data-testid='navigation-menu-toggle-button'
              sx={{
                padding: 0,
                marginRight: '12px',
                color: colors.gray2,
              }}
              onClick={() => {
                if (!row.userId) return;
                handleRemoveGroupMember(row.userId);
              }}
            >
              <DeleteOutlineOutlined />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleClearAddMembersToGroupModalState = () => {
    setIsAddMembersToGroupModalOpen(false);
  };

  return (
    <>
      <AddMembersToGroupModal
        open={isAddMembersToGroupModalOpen}
        setOpen={setIsAddMembersToGroupModalOpen}
        onClose={handleClearAddMembersToGroupModalState}
      />
      <Grid
        sx={{ padding: '12px 24px' }}
        item
        container
        spacing={2}
        display='flex'
        alignContent='flex-start'
      >
        {showEditGroup && activeGroup != null && (
          <Grid item xs={12} sm={6} display='flex' flexDirection='column'>
            <GroupEditForm activeGroup={activeGroup} setShowEditGroup={setShowEditGroup} />
          </Grid>
        )}
        <Grid container item xs={12} gap={2} justifyContent='flex-end'>
          {!showEditGroup && (
            <Button
              sx={{
                marginLeft: '8px',
              }}
              onClick={() => setShowEditGroup(true)}
            >
              {t('MessagesView.group.actions.editGroup')}
            </Button>
          )}
          <Button
            sx={{
              minWidth: '144px',
            }}
            onClick={() => setIsAddMembersToGroupModalOpen(true)}
          >
            {t('MessagesView.group.actions.addMembers')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            rows={getTableRows()}
            getRowId={(row) => row.userId}
            columns={columns}
            paginationModel={{
              page,
              pageSize: 5,
            }}
            loading={isLoadingMessageGroupMembers}
            autoHeight
            columnHeaderHeight={52}
            disableColumnSelector
            disableColumnMenu
            disableRowSelectionOnClick
            pagination
            slots={{
              noRowsOverlay: () => DataGridOverlay(t('Placeholder.noRows')),
              noResultsOverlay: () => DataGridOverlay(t('Placeholder.noResults')),
              loadingOverlay: PageLoader,
              pagination: (props) =>
                CustomPagination({
                  ...props,
                  t,
                  pageCount: Math.ceil(messageGroupMembers.totalElements / 5),
                  pageNo: page,
                  onChange: (_, pageNo) => {
                    handlePageChange(pageNo - 1);
                  },
                }),
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default MessagesGroupEditView;
